import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql } from '@apollo/client';
import { UserInfoContext, type UserInfoContextType } from '@/contexts/UserInfoContext';
import { type UserFeatures, type UserInfo } from '@/types';
import { logger } from '@matrix-forge/shared/utils/logger';
import { publicClient } from '@/utils/apollo';
import { maybeInitSentry } from '@/sentry';

const FEATURES_POLL_INTERVAL_MS = 10_000;

// Query to get user details from server session (authenticated via cookie)
const GET_USER_DETAILS_FOR_SESSION = gql`
  query GetUserDetailsForSession {
    userDetails {
      details
      features {
        automationsToolsEnabled
        caseManagementToolsEnabled
        constructIntegration
        customAgents
        secretsManagementEnabled
        devoAssistantEnabled
        devoToolsEnabled
        externalConnectors
        gitlabToolsEnabled
        knowledgebasesViewOnly
        knowledgebasesEdit
        mcpManagement
        mcpToolSelection
        secOpsToolsEnabled
        sentryUnmaskData
        workflowsManage
        webhooksEnabled
        workflowBuilderEnabled
        asocV2Enabled
        rbacEnabled
        tokenUsageTracking
      }
      roles
      isAdmin
      isPlg
    }
  }
`;

const FEATURE_KEYS_TO_WATCH: (keyof UserFeatures)[] = ['tokenUsageTracking'];

function featuresChanged(prev: UserFeatures | undefined, next: UserFeatures): boolean {
  return FEATURE_KEYS_TO_WATCH.some(key => prev?.[key] !== next[key]);
}

/**
 * A Context Provider to enable descendant nodes to access the UserInfoContext.
 *
 * Session restoration flow:
 * 1. On mount, query the server for user details using the session cookie
 * 2. If the session cookie is valid, server returns user info
 * 3. If no valid session, userInfo remains null -> triggers redirect to /auth/login
 *
 * No JWT is stored in localStorage - authentication is purely cookie-based.
 */
export const UserInfoProvider: FC<PropsWithChildren> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [userMetadata, setUserMetadata] = useState<string | null>(null);
  const [hasRestoredOnce, setHasRestoredOnce] = useState(false);
  const [featuresChangedPending, setFeaturesChangedPending] = useState(false);
  const userInfoRef = useRef<UserInfo | null>(null);

  // On mount: check for valid server session via cookie
  useEffect(() => {
    // Prevent restoration from running multiple times (React Strict Mode in dev)
    if (hasRestoredOnce) return;

    const restoreSession = async () => {
      logger.debug('🔄 UserInfoProvider: Checking server session via cookie...');

      try {
        const { data, error } = await publicClient.query({
          query: GET_USER_DETAILS_FOR_SESSION,
          fetchPolicy: 'network-only',
        });

        if (error) {
          logger.debug('  Server session query failed:', error.message);
          setHasRestoredOnce(true);
          return;
        }

        if (data?.userDetails?.details) {
          logger.debug('  Got server session response');
          const details =
            typeof data.userDetails.details === 'string'
              ? JSON.parse(data.userDetails.details)
              : data.userDetails.details;

          // Extract user info from server response
          const restoredUserInfo: UserInfo = {
            id: details.id || details.userDomain?.id,
            name: details.userName || details.userDomain?.userName || '',
            email: details.email || details.userDomain?.email || '',
            standAloneToken: details.credential?.standAloneToken || '',
            serrea: '',
            adaURI: '',
            alertsURI: '',
            activeboardsURI: '',
            devoDomain: details.domain?.name || details.domainInfo?.name || '',
            jwt: details.jwt || details.credential?.jwtSessionCredentials?.jwtUserId || '',
            features: data.userDetails.features,
            roles: data.userDetails.roles || [],
            isAdmin: data.userDetails.isAdmin ?? false,
            isPlg: data.userDetails.isPlg ?? false,
          };

          logger.debug('✅ Session restored from server!', {
            id: restoredUserInfo.id,
            email: restoredUserInfo.email,
          });

          setUserInfo(restoredUserInfo);
          setUserMetadata(JSON.stringify(details));
        }
      } catch (err: any) {
        // 401 errors are expected when not authenticated
        if (err?.networkError?.statusCode === 401 || err?.message?.includes('Not authenticated')) {
          logger.debug('  No valid server session (not authenticated)');
        } else {
          logger.debug('  Server session check failed:', err?.message || err);
        }
      }

      setHasRestoredOnce(true);
    };

    restoreSession();
  }, [hasRestoredOnce]);

  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const poll = useCallback(async () => {
    if (!userInfoRef.current) return;
    try {
      const { data } = await publicClient.query({
        query: GET_USER_DETAILS_FOR_SESSION,
        fetchPolicy: 'network-only',
      });

      const nextFeatures: UserFeatures | undefined = data?.userDetails?.features;
      if (nextFeatures && featuresChanged(userInfoRef.current?.features, nextFeatures)) {
        logger.debug('[UserInfoProvider] Feature flags changed — applying to UI');
        setUserInfo(prev => (prev ? { ...prev, features: nextFeatures } : prev));
        setFeaturesChangedPending(true);
      }
    } catch {
      // Silent — polling errors should not disrupt the user experience.
    }
  }, []);

  useEffect(() => {
    if (!hasRestoredOnce) return;

    let timerId: ReturnType<typeof setInterval> | null = null;

    const startInterval = () => {
      if (timerId !== null) return;
      timerId = setInterval(poll, FEATURES_POLL_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (timerId === null) return;
      clearInterval(timerId);
      timerId = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        poll();
        startInterval();
      } else {
        stopInterval();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    if (document.visibilityState === 'visible') {
      startInterval();
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopInterval();
    };
  }, [hasRestoredOnce, poll]);

  const dismissFeaturesChanged = useCallback(() => setFeaturesChangedPending(false), []);

  const value = useMemo<UserInfoContextType>(
    () => ({
      userInfo,
      setUserInfo,
      userMetadata,
      setUserMetadata,
      isLoading: !hasRestoredOnce,
      featuresChangedPending,
      dismissFeaturesChanged,
      checkForFeatureChanges: poll,
    }),
    [userInfo, userMetadata, hasRestoredOnce, featuresChangedPending, dismissFeaturesChanged, poll]
  );
  return <UserInfoContext value={value}>{children}</UserInfoContext>;
};

UserInfoProvider.displayName = 'UserInfoProvider';
